/**
 * @file FollowUpAbsenteeismAdherenceListView.jsx
 * @author Fernando Ruano
 * @description Follow Up Absenteeism Adherence List View Page
 */

// Dependencies
import { useTranslation } from 'react-i18next';
import React from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router';

// Helpers
import {
  formatDate,
  joinArrayObject,
  removeNullValuesFromObject,
  sortStringPlus
} from 'commons/helpers/utils';
import {
  createColumns,
  getSearchApiKeys,
  getSelectFilter,
  getSorterString,
  PAGE_LIMIT,
  setBadgeVariation
} from '../helper/utils';
import DashboardDetailList from '../dashboardDetailList';

function FollowUpAbsenteeismAdherenceListView({
  dashboardStaffActions,
  clinicId
}) {
  const { t: translate } = useTranslation();
  const [rowData, setRowData] = React.useState([]);
  const { classification, totalItems: totalItemsParams, phase } = useParams();
  const [totalItems, setTotalItems] = React.useState(totalItemsParams);
  const [dataFilter, setDataFilter] = React.useState(null);
  const [columns, setColumns] = React.useState([]);
  const [filter, setFilter] = React.useState(null);
  const [sorter, setSorter] = React.useState(null);

  const { getFollowUpAbsenteeismAdherenceListPlus } = dashboardStaffActions;

  const additionalColumns = [
    {
      title: translate('Registra nota de TS'),
      dataIndex: 'registra_nota_ts',
      filter: {
        listName: 'lista_registra_nota_ts',
        searchName: 'listaRegistraNotaTs',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus('registra_nota_ts'),
        multiple: 7
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'lista_registra_nota_ts',
            searchName: 'listaRegistraNotaTs'
          },
          dataIndex: 'registra_nota_ts'
        },
        dataFilter
      ),
      ellipsis: true
    },
    {
      title: translate('Tipo de nota'),
      dataIndex: 'tipo_nota',
      filter: {
        listName: 'lista_tipo_nota',
        searchName: 'listaTipoNota',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus('tipo_nota'),
        multiple: 7
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'lista_tipo_nota',
            searchName: 'listaTipoNota'
          },
          dataIndex: 'tipo_nota'
        },
        dataFilter
      ),
      ellipsis: true
    },
    {
      title: translate('Fecha de nota'),
      dataIndex: 'fecha_nota',
      filter: {
        listName: 'lista_fecha_nota_ts',
        searchName: 'listaFechaNotaTs',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus('fecha_nota'),
        multiple: 7
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'lista_fecha_nota_ts',
            searchName: 'listaFechaNotaTs'
          },
          dataIndex: 'fecha_nota'
        },
        dataFilter
      ),
      ellipsis: true
    }
  ];

  const getetData = (offset, pageLimit) => {
    const pageSize = pageLimit || PAGE_LIMIT;
    const joinFilter = joinArrayObject(filter);
    const joinSorter = joinArrayObject(sorter);
    const data = {
      params: {
        idClinic: clinicId,
        registroValoracionPsicologiaUltimos6MesesSeleccionado: classification,
        filterEstadioSeleccionado: phase,
        limit: pageSize,
        offset,
        ...joinFilter,
        ...joinSorter
      }
    };

    getFollowUpAbsenteeismAdherenceListPlus(data, (_, resp) => {
      if (resp?.data && resp.data?.totalRegistros != null) {
        const newData = resp?.data?.listaDetalles?.map((row) => {
          return {
            ...row,
            fecha_nota: formatDate(row.fecha_nota)
          };
        });
        setTotalItems(resp.data?.totalRegistros);
        setRowData(setBadgeVariation(newData ?? []));
        setDataFilter(resp.data);
      }
    });
  };

  const handleTableChange = (pagination, filters, sorterFilter) => {
    if (filters || sorterFilter) {
      const convertedFilters = getSearchApiKeys(
        [...columns, ...additionalColumns],
        removeNullValuesFromObject(filters)
      );
      const convertedSorts = getSorterString(sorterFilter);
      setFilter(convertedFilters);
      setSorter(convertedSorts);
    }
  };

  React.useEffect(() => {
    if (clinicId !== null) getetData(1);
  }, [clinicId]);

  React.useEffect(() => {
    const cols = createColumns(translate, dataFilter);
    setColumns(cols);
  }, [rowData, dataFilter]);

  return (
    <DashboardDetailList
      columns={columns}
      rowData={rowData}
      totalItems={totalItems}
      additionalColumns={additionalColumns}
      onPaginationChange={getetData}
      onChange={handleTableChange}
      filter={filter}
      sorter={sorter}
    />
  );
}

/**
 * Component definition of proptypes.
 */
FollowUpAbsenteeismAdherenceListView.propTypes = {
  dashboardStaffActions: PropTypes.shape({
    getFollowUpAbsenteeismAdherenceListPlus: PropTypes.func.isRequired
  }),
  clinicId: PropTypes.any
};

export default FollowUpAbsenteeismAdherenceListView;
